import { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from 'store/hooks';
import { isPolicySelected } from '../multiplePolicyModal/utils/isPolicySelected';
import { MultiplePolicyModal } from '../multiplePolicyModal';
import { useIsNoData, useGetScreenSize } from 'utils';
import { sendAnalytics, GTM_EVENT_TYPES, GTM_TITLE, analyticsUserType } from 'analytics';
import type { Policy, FullPolicyData } from 'interfaces/customerDataTypes';
import { Loading } from '../loading';
import { useSetSelectedPolicy } from '../multiplePolicyModal/utils/useSetSelectedPolicy';

import './componentWithModal.scss';
import { HpAlert } from '../alert';
import { HpSvgIconTypes } from '@avantia/style-guide';
import classNames from 'classnames';

export interface HpChildComponentProps {
  selectedPolicy: FullPolicyData;
  setShow: Dispatch<SetStateAction<boolean>>;
}

interface ComponentWithModalProps {
  childComponent: ({ selectedPolicy, setShow }: HpChildComponentProps) => JSX.Element;
}

export const ComponentWithModal = ({ childComponent: Component }: ComponentWithModalProps): JSX.Element => {
  const { pathname } = useLocation();
  const policies = useAppSelector((state) => state.customerData.policies);
  const selectedPolicy = useAppSelector((state) => state.customerData.selectedPolicy);
  const selectedPolicyLoading = useAppSelector((state) => state.customerData.policyDetailsLoading);
  const noPolicyFound = useAppSelector((state) => state.customerData.noPolicyFoundError);
  const authentication = useAppSelector((state) => state.authentication);
  const { isAgent } = authentication;
  const redirect = useIsNoData({ policies, checkPolicies: true });
  const { isMobile, isTablet } = useGetScreenSize();

  if (noPolicyFound) {
    return (
      <Container className={classNames('page-container', 'no-policies-found-error')} fluid={isMobile || isTablet}>
        <HpAlert
          alertLevel="error"
          iconType={'exclamation' as HpSvgIconTypes}
          message={<p className="alert-text">Policy not found</p>}
        />
      </Container>
    );
  }

  if (redirect) {
    return redirect;
  }

  useEffect(() => {
    const matchedKey = Object.keys(GTM_TITLE).find((key) => pathname.includes(key));
    const virtualpageTitle = matchedKey ? GTM_TITLE[matchedKey] : undefined;

    sendAnalytics({
      event: GTM_EVENT_TYPES.PAGE_VIEW,
      detail: {
        virtualpage: pathname,
        virtualpage_title: virtualpageTitle,
        virtualpage_url: document.location.href,
        usertype: analyticsUserType(isAgent)
      }
    });
  }, [pathname, isAgent]);

  const { isSelected, policyNumber } = isPolicySelected(pathname, policies as Policy[]);
  const [show, setShow] = useState(!isSelected);

  const policyDetailsId = policies && policies.find((policy) => policy.policyNumber === policyNumber)?.policyDetailsId;
  const historyId = policies && policies.find((policy) => policy.policyNumber === policyNumber)?.historyId;
  useSetSelectedPolicy({
    policyNumber,
    policyDetailsId: policyDetailsId as string,
    selectedPolicy,
    historyId: historyId as number
  });

  return (
    <Container className="page-container" fluid={isMobile || isTablet}>
      {selectedPolicyLoading || !selectedPolicy ? (
        <Loading heading="FETCHING YOUR POLICY" text="Please wait while we gather your policy information" />
      ) : (
        <Component setShow={setShow} selectedPolicy={selectedPolicy} />
      )}
      {show && (
        <MultiplePolicyModal policies={policies as Policy[]} setShow={setShow} selectedPolicy={selectedPolicy} />
      )}
    </Container>
  );
};
